import { useState, useEffect, useCallback } from "react";
import { useDrill } from "../store/drillStore";
import { MODULES, FIELD_LABELS } from "../query/drillPaths";
import { ChevronDown, GitBranch } from "lucide-react";

export function ContextMenu({ menu, onClose }) {
  useEffect(() => {
    if (!menu) return;
    const h = () => onClose();
    window.addEventListener("click", h);
    window.addEventListener("scroll", h, true);
    return () => {
      window.removeEventListener("click", h);
      window.removeEventListener("scroll", h, true);
    };
  }, [menu, onClose]);
  if (!menu) return null;
  return (
    <div className="ctx-menu" style={{ left: Math.min(menu.x, window.innerWidth - 230), top: Math.min(menu.y, window.innerHeight - menu.items.length * 40 - 20) }} data-testid="drill-context-menu">
      <div className="px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold" style={{ color: "var(--slate)" }}>Drill by…</div>
      {menu.items.map((it) => (
        <div key={it.dim} className="ctx-item" data-testid={`ctx-drill-by-${it.dim}`} onClick={it.onClick}>
          <GitBranch size={13} style={{ color: "var(--teal-d)" }} />
          {FIELD_LABELS[it.dim] || it.dim}
        </div>
      ))}
    </div>
  );
}

export function useCtxMenu() {
  const [menu, setMenu] = useState(null);
  const close = useCallback(() => setMenu(null), []);
  return [menu, setMenu, close];
}

export function altDimsFor(moduleKey, seededFields) {
  const cfg = MODULES[moduleKey];
  return cfg.altDims.filter((d) => !seededFields.includes(d));
}

export function Drillable({ module, seed = [], next = null, className = "", children, testId, title }) {
  const openAt = useDrill((s) => s.openAt);
  const [menu, setMenu, close] = useCtxMenu();
  const seededFields = seed.map((e) => e.field);

  const onContextMenu = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const items = altDimsFor(module, seededFields).map((dim) => ({
      dim,
      onClick: (ev) => {
        ev.stopPropagation();
        close();
        openAt(module, seed, dim);
      },
    }));
    if (items.length) setMenu({ x: e.clientX, y: e.clientY, items });
  };

  return (
    <div
      role="button"
      tabIndex={0}
      className={`drillable group relative ${className}`}
      data-drill="true"
      data-testid={testId}
      title={title || "Click to drill down · right-click for drill dimensions"}
      onClick={() => openAt(module, seed)}
      onContextMenu={onContextMenu}
    >
      {children}
      <span className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--teal-d)" }}>
        <ChevronDown size={14} />
      </span>
      <ContextMenu menu={menu} onClose={close} />
    </div>
  );
}
